import { useEffect, useState } from "react";
import { Card, CardHeader, CardBody, Col, Row, Button, Spinner, Badge, Table, Alert } from "reactstrap";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";
import { MasterSheetTable } from "../ArchivedMastersheet/MasterSheetTable";
import { PropertySelector } from "../ArchivedMastersheet/PropertySelector";
import { PreviewTenantForm } from "../ArchivedMastersheet/PreviewTenantForm/PreviewTenantForm";
import {
  getMastersheet,
  getHistoricAsOf,
  getHistoricResidents,
  getHistoricAvailableMonths,
  exportHistoricAsOfExcel,
  exportHistoricResidentsExcel,
  createHistoricSnapshot,
} from "../TenantForm/Helpers/api";

const todayIso = () => new Date().toISOString().slice(0, 10);

const sortByUnitNo = (items) =>
  [...items].sort((a, b) => {
    const an = parseInt(a.unitNo, 10);
    const bn = parseInt(b.unitNo, 10);
    if (!isNaN(an) && !isNaN(bn)) return an - bn;
    return String(a.unitNo || "").localeCompare(String(b.unitNo || ""), undefined, {
      numeric: "base",
      sensitivity: "base",
    });
  });

const HistoricMastersheet = () => {
  const parsedAuthUser = JSON.parse(window.localStorage.getItem("authUser") || "{}");
  const userData = parsedAuthUser.userData || parsedAuthUser;
  const isCompany = userData?.role === "company";

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState({ property_name: "", property_id: "", building_name: "" });
  const [sheetMeta, setSheetMeta] = useState(null); // live mastersheet doc (for _id, property_id, building_name)
  const [mode, setMode] = useState("asof"); // 'asof' | 'residents'
  const [asOfDate, setAsOfDate] = useState(todayIso());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(todayIso());
  const [months, setMonths] = useState([]);
  const [historicSheet, setHistoricSheet] = useState(null); // { ...sheetMeta, items, mastersheetId } for MasterSheetTable
  const [accuracy, setAccuracy] = useState(null); // { source, snapshotUsed, approxCount, orphans, unitCount }
  const [residents, setResidents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [previewTenant, setPreviewTenant] = useState(null);

  const isValidSelection =
    !!filterData.property_id && (filterData.building_list?.length === 0 || !!filterData.building_name);

  useEffect(() => {
    let active = true;
    if (!isValidSelection) return undefined;
    setError(null);
    setHistoricSheet(null);
    setAccuracy(null);
    setResidents(null);
    setSheetMeta(null);
    setMonths([]);
    getMastersheet({ property_id: filterData.property_id, building_name: filterData.building_name })
      .then(({ data }) => {
        if (!active || !data?._id) return;
        setSheetMeta(data);
        return getHistoricAvailableMonths(data._id).then((resp) => {
          if (active && resp?.success) setMonths(resp.data || []);
        });
      })
      .catch((err) => {
        const message = err?.response?.data?.message || "Failed to load mastersheet.";
        if (active && message !== "Building name is required for this property") setError(message);
      });
    return () => {
      active = false;
    };
  }, [filterData.property_id, filterData.building_name]);

  const runQuery = async () => {
    if (!sheetMeta?._id) return;
    setLoading(true);
    setError(null);
    try {
      if (mode === "asof") {
        const resp = await getHistoricAsOf(sheetMeta._id, asOfDate);
        if (!resp?.success) throw new Error(resp?.message || "Failed");
        const rows = resp.data?.rows || [];
        const items = sortByUnitNo(rows.map((r) => r.item).filter(Boolean));
        setHistoricSheet({ ...sheetMeta, items, mastersheetId: sheetMeta._id });
        setAccuracy({
          source: resp.data?.source,
          snapshotUsed: resp.data?.snapshotUsed,
          approxCount: rows.filter((r) => r.accuracy === "approximate").length,
          orphans: resp.data?.reconstruction_orphans || 0,
          unitCount: items.length,
        });
        setResidents(null);
      } else {
        if (!fromDate || !toDate) throw new Error("Pick both From and To dates.");
        const resp = await getHistoricResidents(sheetMeta._id, fromDate, toDate);
        if (!resp?.success) throw new Error(resp?.message || "Failed");
        setResidents(resp.data);
        setHistoricSheet(null);
        setAccuracy(null);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Query failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!sheetMeta?._id) return;
    setDownloading(true);
    try {
      if (mode === "asof") {
        const blob = await exportHistoricAsOfExcel(sheetMeta._id, asOfDate);
        saveAs(new Blob([blob]), `Historic_${filterData.building_name || "sheet"}_${asOfDate}.xlsx`);
      } else {
        const blob = await exportHistoricResidentsExcel(sheetMeta._id, fromDate, toDate);
        saveAs(new Blob([blob]), `WhoResided_${filterData.building_name || "sheet"}.xlsx`);
      }
    } catch (err) {
      Swal.fire("Error", "Excel export failed.", "error");
    } finally {
      setDownloading(false);
    }
  };

  const handleSnapshotNow = async () => {
    if (!sheetMeta?._id) return;
    const confirm = await Swal.fire({
      title: "Take snapshot now?",
      text: `Freeze the current state of ${filterData.building_name || "this mastersheet"} as an exact historical record.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Snapshot now",
    });
    if (!confirm.isConfirmed) return;
    setSnapshotting(true);
    try {
      const resp = await createHistoricSnapshot(sheetMeta._id);
      if (!resp?.success) throw new Error(resp?.message || "Snapshot failed");
      Swal.fire("Done", "Snapshot saved.", "success");
      const monthsResp = await getHistoricAvailableMonths(sheetMeta._id);
      if (monthsResp?.success) setMonths(monthsResp.data || []);
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || err.message, "error");
    } finally {
      setSnapshotting(false);
    }
  };

  const handleEditTenant = (tenantData) => {
    setPreviewTenant(tenantData);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setPreviewTenant(null);
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <PreviewTenantForm
          show={showForm}
          handleClose={handleCloseForm}
          propertyId={filterData.property_id || ""}
          propertyName={filterData.property_name}
          buildingName={filterData.building_name}
          previewData={previewTenant}
          tenantInfo={previewTenant}
        />
        <Row>
          <Col xl={12}>
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center mb-2">
                  <h4 className="mb-0">Historic Mastersheet</h4>
                  {isCompany && (
                    <Button
                      size="sm"
                      color="dark"
                      outline
                      className="ms-auto"
                      onClick={handleSnapshotNow}
                      disabled={!sheetMeta?._id || snapshotting}
                    >
                      {snapshotting ? <Spinner size="sm" /> : "Snapshot now"}
                    </Button>
                  )}
                </div>
                <PropertySelector
                  isFilterOpen={isFilterOpen}
                  setIsFilterOpen={setIsFilterOpen}
                  userData={userData}
                  filterData={filterData}
                  setFilterData={setFilterData}
                />
              </CardHeader>

              <CardBody>
                {!isValidSelection && (
                  <Alert color="danger" className="text-center">
                    <strong>⚠️ Please select both a Property and its corresponding Building to proceed.</strong>
                  </Alert>
                )}

                {isValidSelection && sheetMeta && (
                  <>
                    <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
                      <div className="btn-group">
                        <Button
                          color={mode === "asof" ? "primary" : "outline-primary"}
                          onClick={() => {
                            setMode("asof");
                            setResidents(null);
                          }}
                        >
                          As of date
                        </Button>
                        <Button
                          color={mode === "residents" ? "primary" : "outline-primary"}
                          onClick={() => {
                            setMode("residents");
                            setHistoricSheet(null);
                            setAccuracy(null);
                          }}
                        >
                          Who resided
                        </Button>
                      </div>

                      {mode === "asof" ? (
                        <div>
                          <label className="form-label mb-0 small">Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={asOfDate}
                            max={todayIso()}
                            onChange={(e) => setAsOfDate(e.target.value)}
                          />
                        </div>
                      ) : (
                        <>
                          <div>
                            <label className="form-label mb-0 small">From</label>
                            <input
                              type="date"
                              className="form-control"
                              value={fromDate}
                              max={todayIso()}
                              onChange={(e) => setFromDate(e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="form-label mb-0 small">To</label>
                            <input
                              type="date"
                              className="form-control"
                              value={toDate}
                              max={todayIso()}
                              onChange={(e) => setToDate(e.target.value)}
                            />
                          </div>
                        </>
                      )}

                      <Button color="primary" onClick={runQuery} disabled={loading}>
                        {loading ? <Spinner size="sm" /> : "View"}
                      </Button>
                      <Button
                        color="success"
                        outline
                        onClick={handleExport}
                        disabled={downloading || (!historicSheet && !residents)}
                      >
                        {downloading ? <Spinner size="sm" /> : "Download Excel"}
                      </Button>
                    </div>

                    {mode === "asof" && months.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mb-3">
                        <span className="small text-muted me-1">Month-end snapshots:</span>
                        {months.map((m) => (
                          <Badge
                            key={m.month_key}
                            color="light"
                            className="text-dark border"
                            style={{ cursor: "pointer" }}
                            onClick={() => setAsOfDate(String(m.snapshot_date).slice(0, 10))}
                          >
                            {m.month_key}
                            {m.catch_up ? " (catch-up)" : ""}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {error && <Alert color="danger">{error}</Alert>}

                {accuracy && (
                  <Alert color={accuracy.source === "snapshot" ? "success" : "warning"} className="py-2">
                    <div className="d-flex flex-wrap gap-3 align-items-center">
                      <strong>
                        {accuracy.source === "snapshot"
                          ? "Exact (snapshot-based)"
                          : "Reconstructed (best effort)"}
                      </strong>
                      {accuracy.snapshotUsed && (
                        <span className="small">
                          Base snapshot: {new Date(accuracy.snapshotUsed).toLocaleDateString("en-GB")}
                        </span>
                      )}
                      <span className="small">{accuracy.unitCount} units</span>
                      {accuracy.approxCount > 0 && (
                        <Badge color="warning" className="text-dark">
                          {accuracy.approxCount} approximate unit{accuracy.approxCount > 1 ? "s" : ""}
                        </Badge>
                      )}
                      {accuracy.orphans > 0 && (
                        <Badge color="warning" className="text-dark">
                          {accuracy.orphans} unit{accuracy.orphans > 1 ? "s" : ""} unrecoverable (deleted without archive)
                        </Badge>
                      )}
                    </div>
                  </Alert>
                )}
              </CardBody>

              {historicSheet && historicSheet.items.length === 0 && (
                <CardBody className="pt-0">
                  <p className="text-muted text-center mb-0">No records exist for this date.</p>
                </CardBody>
              )}

              {historicSheet && historicSheet.items.length > 0 && (
                <MasterSheetTable
                  mastersheet={historicSheet}
                  onEditTenant={handleEditTenant}
                  propertyId={filterData.property_id}
                  buildingName={filterData.building_name}
                  onDataUpdate={runQuery}
                  showPreviousVersions={false}
                />
              )}

              {residents && (
                <CardBody className="pt-0">
                  {residents.segments.length === 0 ? (
                    <p className="text-muted text-center mb-0">No one resided here in the selected range.</p>
                  ) : (
                    <Table hover responsive className="table-sm">
                      <thead className="table-light">
                        <tr>
                          <th>Unit #</th>
                          <th>Tenant</th>
                          <th>Occupants</th>
                          <th>Moved In</th>
                          <th>Moved Out</th>
                          <th>Lease Start</th>
                          <th>Lease End</th>
                          <th>Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {residents.segments.map((s, i) => (
                          <tr key={`${s.unitNo}-${s.moveIn}-${i}`}>
                            <td>
                              <strong>{s.unitNo}</strong>
                            </td>
                            <td>{s.tenant}</td>
                            <td>{(s.occupants || []).map((o) => o.name).join(", ") || "—"}</td>
                            <td>{s.moveIn ? new Date(s.moveIn).toLocaleDateString("en-GB") : "—"}</td>
                            <td>
                              {s.moveOut ? (
                                new Date(s.moveOut).toLocaleDateString("en-GB")
                              ) : (
                                <Badge color="success">Current</Badge>
                              )}
                            </td>
                            <td>{s.leaseStart ? new Date(s.leaseStart).toLocaleDateString("en-GB") : "—"}</td>
                            <td>{s.leaseEnd ? new Date(s.leaseEnd).toLocaleDateString("en-GB") : "—"}</td>
                            <td>
                              <Badge
                                color={s.source === "archive" ? "secondary" : "info"}
                                className="text-dark"
                              >
                                {s.source === "archive" ? "Archived" : "Active"}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </CardBody>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export { HistoricMastersheet };
